import { IconEdit, IconTrash } from './icons';
import { useToast } from './ui';
import { api } from '../lib/api';

/**
 * Bearbeiten und Löschen am Ende einer Tabellenzeile.
 *
 * Beides sitzt in jeder Verwaltungstabelle an derselben Stelle und sieht
 * gleich aus. Vorher trug jede Seite ihre eigene Variante – mal mit Text,
 * mal mit Symbol, mal ohne Rückfrage. Wer täglich zwischen den Katalogen
 * wechselt, muss sich nicht jedes Mal neu orientieren.
 *
 * Die Rückfrage vor dem Löschen nennt den Datensatz beim Namen: „Wirklich
 * löschen?“ beantwortet niemand bewusst, „Gewerk ‚Sanitär' löschen?“ schon.
 */
export function RowActions({
  onEdit,
  deletePath,
  label,
  onDeleted,
  confirmText,
  /** Fragt bei Widerspruch der API ein zweites Mal und hängt dann ?force=true an. */
  forceOnConflict = false,
  disabled,
}: {
  onEdit?: () => void;
  deletePath?: string;
  label: string;
  onDeleted?: () => void;
  confirmText?: string;
  forceOnConflict?: boolean;
  disabled?: boolean;
}) {
  const { notify } = useToast();

  const remove = async () => {
    if (!deletePath) return;
    if (!window.confirm(confirmText ?? `„${label}“ wirklich löschen?`)) return;
    try {
      await api.delete(deletePath);
      notify(`„${label}“ gelöscht.`, 'success');
      onDeleted?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Löschen nicht möglich.';
      // Die API meldet Abhängigkeiten als Klartext zurück. Wenn der Aufrufer
      // ein Erzwingen zulässt, wird genau diese Meldung zur zweiten Frage –
      // der Anwender entscheidet mit dem Wissen, was daran hängt.
      if (forceOnConflict && window.confirm(`${message}\n\nTrotzdem löschen?`)) {
        try {
          await api.delete(`${deletePath}${deletePath.includes('?') ? '&' : '?'}force=true`);
          notify(`„${label}“ gelöscht.`, 'success');
          onDeleted?.();
          return;
        } catch (second) {
          notify(second instanceof Error ? second.message : message, 'error');
          return;
        }
      }
      notify(message, 'error');
    }
  };

  return (
    <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
      {onEdit && (
        <button className="btn sm ghost" onClick={onEdit} title={`„${label}“ bearbeiten`} disabled={disabled}>
          <IconEdit width={13} height={13} />
        </button>
      )}
      {deletePath && (
        <button className="btn sm ghost" onClick={remove} title={`„${label}“ löschen`} disabled={disabled}>
          <IconTrash width={13} height={13} />
        </button>
      )}
    </span>
  );
}
